import React from 'react';
import { clsx } from 'clsx';
import { useTheme } from '../../contexts/ThemeContext';

type BrandLogoSize = 'sm' | 'md' | 'lg';

interface BrandLogoProps {
  size?: BrandLogoSize;
  showText?: boolean;
  /** When true, renders the full PNG wordmark logo instead of the icon+text pair */
  wordmark?: boolean;
  className?: string;
  textClassName?: string;
}

const sizeToIconClass: Record<BrandLogoSize, string> = {
  sm: 'h-7 w-7 sm:h-8 sm:w-8',
  md: 'h-9 w-9 sm:h-10 sm:w-10',
  lg: 'h-12 w-12 sm:h-14 sm:w-14',
};

/** Wordmark (PNG) sizes — show the full logo image at appropriate height */
const sizeToWordmarkClass: Record<BrandLogoSize, string> = {
  sm: 'h-7 sm:h-8',
  md: 'h-9 sm:h-10',
  lg: 'h-12 sm:h-14',
};

const BrandLogo: React.FC<BrandLogoProps> = ({
  size = 'md',
  showText = false,
  wordmark = false,
  className,
  textClassName,
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Full wordmark mode — renders the PNG logo at correct proportions
  if (wordmark) {
    return (
      <div className={clsx('flex items-center', className)}>
        <img
          src="/ghdatamarket.jpg"
          alt="GH Data Market"
          className={clsx(
            sizeToWordmarkClass[size],
            'w-auto object-contain block',
            isDark && 'brightness-110 drop-shadow-[0_0_16px_rgba(34,184,92,0.22)]'
          )}
          draggable={false}
        />
      </div>
    );
  }

  return (
    <div className={clsx('flex items-center gap-2 sm:gap-2.5', className)}>
      {/* Icon badge */}
      <div
        className={clsx(
          'flex-shrink-0 flex items-center justify-center',
          size === 'sm' ? 'p-1 rounded-lg' : size === 'lg' ? 'p-2 rounded-2xl' : 'p-1.5 rounded-xl',
          // Light mode: crisp white card with subtle green ring
          'bg-white dark:bg-navy-900/60',
          'ring-1 ring-primary-200/70 dark:ring-primary-500/20',
          'shadow-soft dark:shadow-brand/10',
          'backdrop-blur-sm'
        )}
      >
        <img
          src={isDark ? '/logo-dark.svg' : '/logo.svg'}
          alt="GH Data Market icon"
          className={clsx(
            sizeToIconClass[size],
            'block',
            isDark && 'drop-shadow-[0_0_14px_rgba(34,184,92,0.30)]'
          )}
          draggable={false}
        />
      </div>

      {showText && (
        <div className="min-w-0 flex-1 leading-none">
          {/* "GH" dark + "Data" green + "Market" small muted */}
          <span
            className={clsx(
              'font-bold tracking-tight',
              size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-xl sm:text-2xl' : 'text-base sm:text-lg',
              textClassName
            )}
          >
            <span className="text-navy-900 dark:text-white">GH</span>
            <span className="text-primary-500">Data</span>
          </span>
          <span
            className={clsx(
              'block font-medium text-gray-500 dark:text-gray-400',
              size === 'sm' ? 'text-[10px]' : size === 'lg' ? 'text-sm' : 'text-xs',
            )}
          >
            Market
          </span>
        </div>
      )}
    </div>
  );
};

export default BrandLogo;
